import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import { getTokensByRefreshTokenApi } from 'api/auth';
import { WARRCHIRP_EXPERIENCE_API_URL } from 'config';
import { StorageE } from 'declarations/enums/storage';
import type { ApiErrorDataI, ApiErrorI } from 'declarations/interfaces/errors';
import useAuthStore from 'hooks/store/useAuthStore';

const ApiError = (error: AxiosError<ApiErrorDataI>): ApiErrorI => ({
  name: error.name,
  code: error.code || null,
  data: error.response?.data || null,
});

const axiosInstance = axios.create({
  baseURL: `${WARRCHIRP_EXPERIENCE_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// An interceptor for Authorization header
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem(StorageE.IdToken);

    if (token && config.baseURL === WARRCHIRP_EXPERIENCE_API_URL && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

// Refresh authorization function
const refreshAuthLogic = async (
  failedRequest: AxiosError & { response: { config: InternalAxiosRequestConfig } }
): Promise<void> => {
  const refreshToken = localStorage.getItem(StorageE.RefreshToken);

  if (refreshToken) {
    return getTokensByRefreshTokenApi(refreshToken)
      .then((response): void => {
        localStorage.setItem(StorageE.IdToken, response.id_token);

        failedRequest.response.config.headers.Authorization = `Bearer ${response.id_token}`;
      })
      .catch((error): Promise<never> => {
        localStorage.removeItem(StorageE.IdToken);
        localStorage.removeItem(StorageE.RefreshToken);
        useAuthStore.setState({ isUserLoggedIn: false });
        // Always reject with a proper Error to prevent "Unknown promise rejection reason"
        // (ApiError and other non-Error objects lack .message and cause the overlay to fail)
        const err =
          error instanceof Error
            ? error
            : new Error(
                (error as { message?: string })?.message ??
                  (error as { data?: { title?: string } })?.data?.title ??
                  'Failed to refresh authentication token'
              );
        return Promise.reject(err);
      });
  }

  // No refresh token available - reject with proper error
  return Promise.reject(new Error('No refresh token available'));
};

createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Axios accepts any serializable data type (objects, arrays, URLSearchParams, FormData, etc.)
export async function get<ResponseI, RequestParamsI = any>(
  path: string,
  params?: RequestParamsI,
  config?: AxiosRequestConfig
): Promise<import('axios').AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.get<ResponseI>(path, {
      params: params || {},
      ...config,
    });

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Axios accepts any serializable data type (objects, arrays, URLSearchParams, FormData, strings, etc.)
export async function post<ResponseI, RequestBodyI = any>(
  path: string,
  data?: RequestBodyI,
  config?: AxiosRequestConfig
): Promise<import('axios').AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.post<ResponseI>(path, data, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Axios accepts any serializable data type (objects, arrays, URLSearchParams, FormData, strings, etc.)
export async function put<ResponseI, RequestBodyI = any>(
  path: string,
  data?: RequestBodyI,
  config?: AxiosRequestConfig
): Promise<import('axios').AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.put<ResponseI>(path, data, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

export async function remove<ResponseI>(
  path: string,
  config?: AxiosRequestConfig
): Promise<import('axios').AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.delete<ResponseI>(path, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}
